import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getIsWhiteLabel, getWhiteLabelLogo } from '@/redux/modules/whiteLabel';
import { isUserDataLoading, isUserLoggedIn } from '@/redux/modules/user';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import Crown from './Crown';
import DiscoveryTags from '@/components/DiscoveryTags/DiscoveryTags';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import LoggedInMenu from './Login/LoggedInMenu';
import LoggedOut from './Login/LoggedOut';
import MobileNavigation from './Navigation/MobileNavigation';
import Navigation from './Navigation/Navigation';
import SearchInput from '@/components/SearchInput/SearchInput';
import styled, { css, useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

const Header = () => {
    const location = useLocation();
    const logo = useAppSelector(getWhiteLabelLogo, isEqual);
    const userDataLoading = useAppSelector(isUserDataLoading, isEqual);
    const userLoggedIn = useAppSelector(isUserLoggedIn, isEqual);
    const whiteLabel = useAppSelector(getIsWhiteLabel, isEqual);

    const {
        breakpoints: { tabletNarrowWidthNum, tabletWidthNum },
    } = useTheme();

    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum);
    const useSmallLogo = useIsNarrowerThan(tabletWidthNum + 1);
    const searchBarDesktop = 'search-bar-desktop';
    const searchBarMobile = 'search-bar-mobile';

    // split them into two to prevent header weirdness
    if (isMobile) {
        return (
            <StyledHeader id="mobile-header" key="mobile-header">
                <StyledWrapper key="search-bar-wrapper" whiteLabel={whiteLabel}>
                    <StyledSearchBar key="search-bar" loggedIn={userLoggedIn}>
                        <StyledImageContainer>
                            {!whiteLabel && <MobileNavigation key="mobile-navigation" />}
                            <StyledHeaderLogoLink key="logo-link" route to="/">
                                {Boolean(logo) &&
                                    (whiteLabel ? (
                                        <StyledHeaderLogo alt="logo" src={logo} />
                                    ) : (
                                        <StyledLALogo alt="logo" src={logo} width={useSmallLogo ? '121' : '181'} />
                                    ))}
                            </StyledHeaderLogoLink>
                        </StyledImageContainer>
                        <StyledSearchFieldContainer key="search-field-container">
                            {/* special element for Jasper52 */}
                            <StyledExceptionalItems id="exceptional-items">
                                <FormattedMessage id="header.j52" />
                            </StyledExceptionalItems>
                        </StyledSearchFieldContainer>
                        <StyledControls key="controls-container">
                            {userLoggedIn || userDataLoading ? <LoggedInMenu parent={this} /> : <LoggedOut />}
                        </StyledControls>
                    </StyledSearchBar>
                    <StyledMobileSearch key="mobile-search-bar">
                        <SearchInput
                            id="mobile-search"
                            key="mobile-search"
                            location={location}
                            name={searchBarMobile}
                            reverseButton
                        />
                    </StyledMobileSearch>
                </StyledWrapper>
                {!whiteLabel && <StyledTags isInHeader key="discoveryTags" width={1024} />}
            </StyledHeader>
        );
    }

    return (
        <StyledHeader id="desktop-header" key="desktop-header">
            <Crown key="crown" />
            <StyledWrapper key="search-bar-wrapper" whiteLabel={whiteLabel}>
                <StyledSearchBar key="search-bar" loggedIn={userLoggedIn} whiteLabel={whiteLabel}>
                    <StyledImageContainer $whiteLabel={whiteLabel}>
                        <StyledHeaderLogoLink $whiteLabel={whiteLabel} key="logo-link" route to="/">
                            {Boolean(logo) &&
                                (whiteLabel ? (
                                    <StyledHeaderLogo alt="logo" src={logo} />
                                ) : (
                                    <StyledLALogo
                                        alt="logo"
                                        height="24.13"
                                        src={logo}
                                        width={useSmallLogo ? '121' : '181'}
                                    />
                                ))}
                        </StyledHeaderLogoLink>
                    </StyledImageContainer>
                    <StyledSearchFieldContainer key="search-field-container">
                        {/* special element for Jasper52 */}
                        <StyledExceptionalItems id="exceptional-items">
                            <FormattedMessage id="header.j52" />
                        </StyledExceptionalItems>
                        <SearchInput
                            id="desktop-search"
                            key="desktop-search"
                            location={location}
                            name={searchBarDesktop}
                        />
                    </StyledSearchFieldContainer>
                    <StyledControls key="controls-container">
                        {userLoggedIn || userDataLoading ? <LoggedInMenu parent={this} /> : <LoggedOut />}
                    </StyledControls>
                </StyledSearchBar>
            </StyledWrapper>
            {!whiteLabel && <Navigation key="desktop-navigation" />}
        </StyledHeader>
    );
};

export default Header;

const StyledHeader = styled.div`
    @media print {
        display: none;
    }

    div[class='popover-content'] {
        padding: 10px 0 0;
    }

    @media (max-width: 767px) {
        margin-top: 5px;
    }
`;

const StyledControls = styled.div`
    height: 44px;
    flex: 0 0 auto;
    margin-left: 0;
    display: flex;
`;

interface WrapperProps {
    whiteLabel: boolean;
}

const StyledWrapper = styled.div<WrapperProps>`
    position: relative;
    z-index: 13;
    ${({ whiteLabel }) =>
        whiteLabel
            ? css`
                  max-width: 1240px;
                  margin: 0 auto 55px;
              `
            : ''};
`;
interface SearchBarProps {
    loggedIn: boolean;
    whiteLabel?: boolean;
}

const StyledSearchBar = styled.div<SearchBarProps>`
    display: flex;
    padding: ${({ whiteLabel }) => (whiteLabel ? '20px' : '13px 20px')};

    @media (min-width: 1240px) {
        margin: 0 auto;
        max-width: 1240px;
    }

    @media (max-width: 767px) {
        padding: 2px 20px 0;
        ${({ loggedIn }) =>
            loggedIn
                ? css`
                      padding: 2px 8px 0 20px;
                  `
                : ''};
    }
`;

interface ImageContainerProps {
    $whiteLabel?: boolean;
}

const StyledImageContainer = styled.div<ImageContainerProps>`
    flex: 0 1 ${({ $whiteLabel }) => ($whiteLabel ? '320px' : '181px')};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: -15px 10px -15px 0;
    position: relative;

    @media (max-width: 991px) {
        flex: 0 1 ${({ $whiteLabel }) => ($whiteLabel ? '220px' : '123px')};
    }

    @media (max-width: 767px) {
        flex: 1 1 auto;
    }

    & > div {
        padding: 0 20px 0 0;
        font-size: 20px;
    }
`;

const StyledHeaderLogoLink = styled(Link)<ImageContainerProps>`
    height: 74px;
    overflow: hidden;
    width: ${({ $whiteLabel }) => ($whiteLabel ? '100%' : '181px')};
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 991px) {
        max-height: 64px;
        max-width: 123px;
    }
`;

const StyledHeaderLogo = styled.img`
    max-height: 100%;
    max-width: 100%;
    display: block;

    @media (max-width: 767px) {
        padding-top: 0;
    }

    @media (max-width: 991px) {
        max-height: 64px;
        max-width: 121px;
    }
`;

const StyledLALogo = styled.img`
    width: 181px;
    height: 24.13px;
    display: block;

    @media (max-width: 767px) {
        padding-top: 0;
    }

    @media (max-width: 991px) {
        max-height: 64px;
        max-width: 121px;
    }
`;

const StyledSearchFieldContainer = styled.div`
    height: 44px;
    margin: 0 12px 0 15px;
    flex: 1 1 auto;
    position: relative;
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: block;
        margin: 0 12px 0 5px;
    }

    @media (min-width: 769px) {
        margin: 0 6px 0 26px;
    }
`;

const StyledExceptionalItems = styled.div`
    font-size: 15px !important;
    color: ${({ theme }) => theme.colors.red100};
    font-family: ${({ theme }) => theme.typography.fontPrimary} !important;
    font-style: italic;
    flex: 1 1 500px;
    display: none;
    align-items: center;
    margin-right: 20px;
`;

const StyledMobileSearch = styled.div`
    transition: border-bottom 0.3s, overflow 0.1s ease 0.5s, padding 0.3s, height 0.3s;
    padding: 6px 20px 10px;
    height: 55px !important;
    overflow: inherit;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const StyledTags = styled(DiscoveryTags)`
    && {
        position: relative;
        z-index: 1;
        padding: 0;
        overflow: hidden;
        margin: 0 auto;
        background-color: ${({ theme }) => theme.colors.white};

        div:first-child {
            margin: 0;
            width: 100%;
            display: inline-block;
            white-space: nowrap;
            overflow: -moz-scrollbars-none;
            -ms-overflow-style: none;
            overflow-x: auto;
            overflow-y: hidden;
            height: 55px;
            > * {
                color: ${({ theme }) => theme.colors.grey100};
                background-color: transparent;
                border-radius: 2px;
                height: 36px;
                display: inline-flex;
                padding: 0 8px;
                align-items: center;
                font-family: ${({ theme }) => theme.typography.fontSecondary};
                font-size: 14px;
                letter-spacing: 0.5px;
                text-decoration: none;
                box-shadow: none;
                margin-right: 0;

                &:hover {
                    background-color: transparent;
                    color: ${({ theme }) => theme.colors.grey100};
                }
            }
        }

        .tags::-webkit-scrollbar {
            display: none !important;
        }
    }
`;
